interface SheetProps {
  /** Whether the browser has granted location access (`canUseLocation`). */
  isGranted: boolean;
  /**
   * Elapsed minutes since the last position fix, snapshotted at sheet-open
   * time by the parent. `null` means no timestamp is available.
   */
  minutesAgo: number | null;
  /** Called when the user confirms they want to disable location. */
  onDisable: () => void;
  /** Called when the sheet should close with no side effects. */
  onClose: () => void;
}

/**
 * Bottom sheet that slides up when the user taps the {@link LocationStatusBar}.
 *
 * **Granted state**: shows the active indicator, last-updated elapsed time,
 * a brief explanation, and a "Disable location" button.
 *
 * **Denied state**: shows re-enable instructions (browser lock icon flow) and
 * an iOS-specific Safari callout. There is intentionally no "Open device
 * settings" button — this API does not exist on mobile web.
 *
 * Escape key and backdrop tap both call `onClose` with no side effects.
 * The `autoFocus` on the close button ensures keyboard events are captured
 * by the dialog's `onKeyDown` handler via bubbling.
 */
function formatUpdated(minutesAgo: SheetProps["minutesAgo"]): string | null {
  if (minutesAgo == null) return null;
  if (minutesAgo === 0) return "Updated just now";
  return `Updated ${String(minutesAgo)} min ago`;
}

export function LocationSettingsSheet({
  isGranted,
  minutesAgo,
  onDisable,
  onClose,
}: SheetProps) {
  const updatedLabel = formatUpdated(minutesAgo);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Location settings"
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[59] bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Sheet */}
      <div className="fixed inset-x-0 bottom-0 z-[60] rounded-t-2xl bg-white p-6 pl-[calc(1.5rem+env(safe-area-inset-left,0px))] pr-[calc(1.5rem+env(safe-area-inset-right,0px))] pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] shadow-xl">
        <button
          autoFocus
          aria-label="Close location settings"
          onClick={onClose}
          className="absolute top-4 right-[calc(1rem+env(safe-area-inset-right,0px))] p-1 text-zinc-400"
        >
          ✕
        </button>

        {isGranted ? (
          <>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
              <span className="font-semibold text-green-700">Active</span>
            </div>
            {updatedLabel && (
              <p className="text-sm text-zinc-500 mb-4">{updatedLabel}</p>
            )}
            <p className="text-sm text-zinc-600 mb-6">
              Via LA is using your location to show nearby stops and routes.
            </p>
            <button
              onClick={onDisable}
              className="w-full py-3 rounded-xl bg-zinc-100 text-zinc-700 text-sm font-medium"
            >
              Disable location
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-2.5 h-2.5 rounded-full bg-zinc-400" />
              <span className="font-semibold text-zinc-600">Off</span>
            </div>
            <p className="text-sm text-zinc-700 mb-3">
              To re-enable location access in your browser:
            </p>
            <ol className="text-sm text-zinc-600 space-y-1 mb-4 list-decimal list-inside">
              <li>Tap the browser lock icon in the address bar</li>
              <li>
                Tap <strong>Location</strong>
              </li>
              <li>
                Select <strong>Allow</strong>
              </li>
              <li>Refresh the page</li>
            </ol>
            <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-4 text-sm text-zinc-600">
              <p className="font-medium text-zinc-700 mb-1">
                On iPhone (Safari)
              </p>
              <p>
                Go to <strong>Settings → Safari → Location</strong> and select{" "}
                <strong>Allow</strong>.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
